/**
 * StudioBlockLibrary.tsx — Biblioteca de blocos do construtor (coluna esquerda)
 *
 * Lista os blocos disponíveis agrupados por família, já filtrados pelas formas
 * de entrega escolhidas no modelo. A lista vem SEMPRE do catálogo único do
 * Studio Core — esta tela não mantém lista própria de blocos.
 *
 * RELEASE: v1.1.0 - 2026-09-10 - Filtra blocos também pelo TIPO do modelo: o
 *          bloco de formulário só existe em Landing Page (a Proposta Online não
 *          captura leads).
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 4)
 */

import React, { useMemo, useState } from 'react';
import { Search, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import {
  listBlocks,
  type StudioBlockFamily,
  type StudioOutput,
  type StudioTemplateType,
} from '@/lib/studio/core';

/** Rótulos das famílias de bloco, na ordem de exibição. */
const FAMILIAS: Array<{ key: StudioBlockFamily; label: string }> = [
  { key: 'layout', label: 'Estrutura' },
  { key: 'content', label: 'Conteúdo' },
  { key: 'data', label: 'Dados' },
  { key: 'action', label: 'Ações' },
];

/** Blocos permitidos apenas em determinados tipos de modelo. */
const BLOCOS_EXCLUSIVOS: Record<string, StudioTemplateType[]> = {
  'page.form': ['landing_page'],
};

interface StudioBlockLibraryProps {
  /** Formas de entrega do modelo — filtram os blocos compatíveis. */
  outputs: StudioOutput[];
  /** Tipo do modelo — restringe blocos exclusivos (ex.: formulário). */
  templateType?: StudioTemplateType;
  /** Insere o bloco no final do documento. */
  onAdd: (blockKey: string) => void;
}

export function StudioBlockLibrary({ outputs, templateType, onAdd }: StudioBlockLibraryProps) {
  const [busca, setBusca] = useState('');

  const grupos = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return FAMILIAS.map((familia) => ({
      ...familia,
      itens: listBlocks({ family: familia.key }).filter((bloco) => {
        // O bloco precisa saber se desenhar em TODAS as saídas escolhidas
        const compativel = outputs.every((o) => bloco.outputs.includes(o));
        const exclusivo = BLOCOS_EXCLUSIVOS[bloco.key];
        const tipoPermitido =
          !exclusivo || (templateType ? exclusivo.includes(templateType) : false);
        const casaBusca = !termo || bloco.label.toLowerCase().includes(termo);
        return compativel && tipoPermitido && casaBusca;
      }),
    })).filter((g) => g.itens.length > 0);
  }, [busca, outputs, templateType]);


  return (
    <div className="flex h-full flex-col border-r bg-muted/20">
      <div className="border-b p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar bloco"
            className="h-8 pl-7 text-xs"
          />
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="space-y-4 p-3">
          {grupos.map((grupo) => (
            <div key={grupo.key}>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {grupo.label}
              </p>
              <div className="space-y-1">
                {grupo.itens.map((bloco) => (
                  <Button
                    key={bloco.key}
                    type="button"
                    variant="ghost"
                    onClick={() => onAdd(bloco.key)}
                    className="h-auto w-full justify-start gap-2 rounded-md border bg-background px-2 py-2 text-left text-xs font-normal hover:bg-accent"
                  >
                    <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="truncate">{bloco.label}</span>
                  </Button>
                ))}
              </div>
            </div>
          ))}

          {grupos.length === 0 && (
            <p className="px-1 py-6 text-center text-xs text-muted-foreground">
              Nenhum bloco compatível com as formas de entrega escolhidas.
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
